"use client";

import React, { useState, useMemo } from 'react';
import { Search, Download, Filter, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Shield, CheckCircle, XCircle, AlertTriangle, Eye, Trash2, MoreHorizontal, FileText, FileJson, Loader2 } from 'lucide-react';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { toast } from 'sonner';
import { useAuditLogs, AuditLogRow } from '@/lib/supabase/realtime-hooks';
import { auditLog } from '@/lib/supabase/audit-logger';
import HeroBanner from '@/components/HeroBanner';
import { useTapRipple } from '@/hooks/useMobileInteractions';

type SortField = keyof AuditLogRow;
type SortDir = 'asc' | 'desc';

const riskBadge: Record<string, string> = {
  critical: 'cyber-badge-critical',
  high: 'cyber-badge-high',
  medium: 'cyber-badge-medium',
  low: 'cyber-badge-low',
  info: 'cyber-badge-info',
};

const outcomeConfig: Record<string, { color: string; icon: React.ReactNode }> = {
  success: { color: 'text-green-400 bg-green-500/10 border border-green-500/25', icon: <CheckCircle size={11} /> },
  failed: { color: 'text-cyber-red bg-red-500/10 border border-red-500/25', icon: <XCircle size={11} /> },
  blocked: { color: 'text-orange-400 bg-orange-500/10 border border-orange-500/25', icon: <AlertTriangle size={11} /> },
};

const mfaBadge: Record<string, string> = {
  verified: 'text-cyber-green bg-green-500/10 border border-green-500/25',
  bypass: 'text-cyber-red bg-red-500/10 border border-red-500/25',
  failed: 'text-orange-400 bg-orange-500/10 border border-orange-500/25',
  'n/a': 'text-cyber-muted bg-white/5 border border-cyber-border',
};

const actionTypeColor: Record<string, string> = {
  auth: 'text-cyber-blue',
  data: 'text-cyber-purple',
  config: 'text-yellow-400',
  threat: 'text-cyber-red',
  admin: 'text-orange-400',
};

function formatTimestamp(iso: string) {
  const d = new Date(iso);
  const date = d.toISOString().split('T')[0];
  const time = d.toTimeString().split(' ')[0];
  return { date, time };
}

// ─── Mobile Log Card ──────────────────────────────────────────────────────────

interface MobileLogCardProps {
  log: AuditLogRow;
  selected: boolean;
  onToggle: () => void;
}

function MobileLogCard({ log, selected, onToggle }: MobileLogCardProps) {
  const [expanded, setExpanded] = useState(false);
  const ts = formatTimestamp(log.event_timestamp);
  const { triggerRipple } = useTapRipple();

  return (
    <div
      className={`ripple-container cyber-card p-3 space-y-2 transition-colors press-scale ${selected ? 'border-cyber-blue/40 bg-cyber-blue/5' : ''}`}
      onPointerDown={triggerRipple}
      role="row"
      aria-selected={selected}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            aria-label={`Select log ${log.log_id}`}
            className="rounded border-cyber-border accent-cyber-blue cursor-pointer shrink-0 mt-0.5"
          />
          <div className="min-w-0">
            <p className={`text-xs font-medium truncate ${actionTypeColor[log.action_type] || 'text-cyber-text'}`}>
              {log.action}
            </p>
            <p className="text-[10px] text-cyber-muted font-mono truncate">{log.actor} · {log.actor_role}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={riskBadge[log.risk_level] || 'cyber-badge-info'}>
            {log.risk_level}
          </span>
          <button
            onClick={() => setExpanded(!expanded)}
            onPointerDown={(e) => { e.stopPropagation(); triggerRipple(e); }}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse log details' : 'Expand log details'}
            className="ripple-container press-scale-sm w-6 h-6 rounded-md bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors text-cyber-muted"
          >
            {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
        </div>
      </div>

      {/* Always-visible row */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-[10px] font-mono font-medium rounded-md px-2 py-0.5 ${outcomeConfig[log.outcome]?.color || ''}`}>
            {outcomeConfig[log.outcome]?.icon}
            {log.outcome}
          </span>
          <span className={`text-[10px] font-mono font-medium border rounded-md px-2 py-0.5 ${mfaBadge[log.mfa_status] || ''}`}>
            {log.mfa_status}
          </span>
        </div>
        <div className="text-right">
          <p className="font-mono text-[10px] text-cyber-text">{ts.time}</p>
          <p className="font-mono text-[9px] text-cyber-muted">{ts.date}</p>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="pt-2 border-t border-cyber-border/50 space-y-1.5 text-[11px] font-mono">
          <div className="flex justify-between gap-2">
            <span className="text-cyber-muted">Resource</span>
            <span className="text-cyber-text truncate max-w-[60%] text-right">{log.resource}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-cyber-muted">IP Address</span>
            <span className="text-cyber-text">{log.ip_address}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-cyber-muted">Location</span>
            <span className="text-cyber-text">{log.location}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-cyber-muted">Session</span>
            <span className="text-cyber-subtle truncate max-w-[60%] text-right">{log.session_id}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-cyber-muted">Log ID</span>
            <span className="text-cyber-subtle truncate max-w-[60%] text-right">{log.log_id}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SecurityAuditLogsContent() {
  const { language } = useLanguage();
  const { logs, loading, error } = useAuditLogs();
  const { triggerRipple } = useTapRipple();

  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('event_timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingJSON, setExportingJSON] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filtered = useMemo(() => {
    return logs.filter(log => {
      const matchSearch = search === '' ||
        log.actor?.toLowerCase().includes(search.toLowerCase()) ||
        log.action?.toLowerCase().includes(search.toLowerCase()) ||
        log.resource?.toLowerCase().includes(search.toLowerCase()) ||
        log.ip_address?.includes(search) ||
        log.log_id?.toLowerCase().includes(search.toLowerCase());
      const matchSeverity = severityFilter === 'all' || log.risk_level === severityFilter;
      const matchOutcome = outcomeFilter === 'all' || log.outcome === outcomeFilter;
      return matchSearch && matchSeverity && matchOutcome;
    }).sort((a, b) => {
      const aVal = (a[sortField] as string) || '';
      const bVal = (b[sortField] as string) || '';
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [logs, search, severityFilter, outcomeFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const toggleRow = (id: string) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === paginated.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginated.map(l => l.id)));
    }
  };

  const handleExportCSV = async () => {
    setExportingCSV(true);
    await new Promise(resolve => setTimeout(resolve, 600));
    const exportTimestamp = new Date().toISOString();
    const complianceHeaders = [
      `# BD Compliance Security Audit Log Export`,
      `# Generated: ${exportTimestamp}`,
      `# Regulation: BD Digital Security Act §18 — Audit Log Retention`,
      `# Organization: Otek1 Syber Security Platform`,
      `# Export Scope: Filtered Records (${filtered.length} entries)`,
      `# Filters Applied: Severity=${severityFilter} | Outcome=${outcomeFilter} | Search="${search}"`,
      `# Classification: CONFIDENTIAL — Internal Use Only`,
      `#`,
    ];

    const csvColumns = [
      'Log ID', 'Timestamp (UTC)', 'Actor', 'Actor Role', 'Action', 'Action Type',
      'Resource', 'IP Address', 'Location', 'Risk Level', 'MFA Status', 'Session ID', 'Outcome',
    ];

    const csvRows = filtered.map(log => [
      log.log_id ?? '',
      log.event_timestamp ?? '',
      log.actor ?? '',
      log.actor_role ?? '',
      log.action ?? '',
      log.action_type ?? '',
      log.resource ?? '',
      log.ip_address ?? '',
      log.location ?? '',
      log.risk_level ?? '',
      log.mfa_status ?? '',
      log.session_id ?? '',
      log.outcome ?? '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

    const csvContent = [
      ...complianceHeaders,
      csvColumns.join(','),
      ...csvRows,
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bd-compliance-audit-logs-${exportTimestamp.replace(/[:.]/g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    await auditLog.exportCSV(filtered.length, `severity=${severityFilter},outcome=${outcomeFilter}`);
    toast.success('CSV export complete', {
      description: `${filtered.length} records exported with BD Compliance §18 headers`,
    });
    setExportingCSV(false);
  };

  const handleExportJSON = async () => {
    setExportingJSON(true);
    await new Promise(resolve => setTimeout(resolve, 600));
    const exportTimestamp = new Date().toISOString();
    const exportPayload = {
      compliance_metadata: {
        report_title: 'BD Compliance Security Audit Log Export',
        generated_at: exportTimestamp,
        regulation: 'BD Digital Security Act §18 — Audit Log Retention',
        organization: 'Otek1 Syber Security Platform',
        classification: 'CONFIDENTIAL — Internal Use Only',
        export_scope: `Filtered Records`,
        total_records: filtered.length,
        filters_applied: {
          severity: severityFilter,
          outcome: outcomeFilter,
          search_query: search,
        },
      },
      audit_logs: filtered.map(log => ({
        log_id: log.log_id,
        event_timestamp: log.event_timestamp,
        actor: log.actor,
        actor_role: log.actor_role,
        action: log.action,
        action_type: log.action_type,
        resource: log.resource,
        ip_address: log.ip_address,
        location: log.location,
        risk_level: log.risk_level,
        mfa_status: log.mfa_status,
        session_id: log.session_id,
        outcome: log.outcome,
      })),
    };

    const jsonContent = JSON.stringify(exportPayload, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bd-compliance-audit-logs-${exportTimestamp.replace(/[:.]/g, '-')}.json`;
    link.click();
    URL.revokeObjectURL(url);

    await auditLog.exportJSON(filtered.length);
    toast.success('JSON export complete', {
      description: `${filtered.length} records exported with BD Compliance metadata`,
    });
    setExportingJSON(false);
  };

  const handleBulkDelete = () => {
    toast.error(`${selectedRows.size} logs flagged for review`);
    setSelectedRows(new Set());
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={10} className="text-cyber-subtle opacity-50" />;
    return sortDir === 'asc'
      ? <ChevronUp size={10} className="text-cyber-blue" />
      : <ChevronDown size={10} className="text-cyber-blue" />;
  };

  // Stats derived from live data
  const statsData = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayLogs = logs.filter(l => l.event_timestamp?.startsWith(today));
    return {
      total: todayLogs.length || logs.length,
      critical: logs.filter(l => l.risk_level === 'critical').length,
      blocked: logs.filter(l => l.outcome === 'blocked').length,
      failedAuth: logs.filter(l => l.action_type === 'auth' && l.outcome === 'failed').length,
    };
  }, [logs]);

  return (
    <div className="space-y-4 md:space-y-5 swipe-in-right">
      <HeroBanner variant="compact" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={`text-xl md:text-2xl font-bold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'logs_title')}
          </h1>
          <p className={`text-sm text-cyber-muted mt-0.5 ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'logs_subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-mono text-cyber-muted">
            <Shield size={11} className="text-cyber-blue" />
            <span>BD §18 Compliant</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] font-mono">
            <div className={`w-1.5 h-1.5 bg-cyber-blue rounded-full animate-pulse ${loading ? 'bg-yellow-400 animate-pulse' : 'live-dot'}`} />
            <span className="text-cyber-muted">{loading ? 'LOADING' : 'LIVE'}</span>
          </div>
          <button
            onClick={handleExportCSV}
            onPointerDown={(e) => triggerRipple(e)}
            disabled={exportingCSV || loading}
            aria-label="Export filtered audit logs as CSV"
            className="ripple-container press-scale flex items-center gap-2 px-3 py-1.5 bg-cyber-blue/15 border border-cyber-blue/30 text-cyber-blue rounded-lg text-xs font-medium hover:bg-cyber-blue/25 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {exportingCSV ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
            <span className={language === 'bn' ? 'font-bangla' : ''}>{exportingCSV ? 'Exporting...' : 'CSV'}</span>
          </button>
          <button
            onClick={handleExportJSON}
            onPointerDown={(e) => triggerRipple(e)}
            disabled={exportingJSON || loading}
            aria-label="Export filtered audit logs as JSON"
            className="ripple-container press-scale flex items-center gap-2 px-3 py-1.5 bg-cyber-purple/15 border border-cyber-purple/30 text-cyber-purple rounded-lg text-xs font-medium hover:bg-cyber-purple/25 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {exportingJSON ? <Loader2 size={13} className="animate-spin" /> : <FileJson size={13} />}
            <span className={language === 'bn' ? 'font-bangla' : ''}>{exportingJSON ? 'Exporting...' : 'JSON'}</span>
          </button>
        </div>
      </div>

      {/* Stats Row — 2 cols on mobile, 4 on md+ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="cyber-card px-4 py-3 flex items-center gap-3" aria-busy="true">
              <div className="flex flex-col gap-2 w-full">
                <div className="w-20 h-2.5 rounded skeleton-cyber" />
                <div className="w-12 h-6 rounded skeleton-cyber" />
              </div>
            </div>
          ))
        ) : (
          [
            { label: 'Total Today', value: statsData.total.toLocaleString(), color: 'text-cyber-blue' },
            { label: 'Critical Events', value: String(statsData.critical), color: 'text-cyber-red' },
            { label: 'Blocked', value: String(statsData.blocked), color: 'text-orange-400' },
            { label: 'Failed Auth', value: String(statsData.failedAuth), color: 'text-yellow-400' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="ripple-container cyber-card px-4 py-3 flex items-center gap-3 press-scale cursor-default"
              onPointerDown={(e) => triggerRipple(e)}
            >
              <div>
                <p className="text-[10px] text-cyber-muted font-mono uppercase tracking-widest">{stat.label}</p>
                <p className={`text-xl font-bold font-mono tabular-nums mt-0.5 ${stat.color}`}>{stat.value}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Filters — stacked on mobile */}
      <div className="cyber-card px-4 py-3 flex flex-wrap items-center gap-3" role="search" aria-label="Filter audit logs">
        {/* Search — full width on mobile */}
        <div className="flex items-center gap-2 bg-white/5 border border-cyber-border rounded-lg px-3 py-1.5 w-full sm:flex-1 sm:min-w-[200px] hover:border-cyber-blue/30 transition-colors group">
          <Search size={13} className="text-cyber-muted group-hover:text-cyber-blue transition-colors shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder={t(language, 'logs_search')}
            aria-label="Search audit logs by actor, action, resource, IP, or log ID"
            className={`bg-transparent text-xs text-cyber-text placeholder-cyber-muted outline-none flex-1 font-mono ${language === 'bn' ? 'font-bangla placeholder:font-bangla' : ''}`}
          />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search" className="text-cyber-muted hover:text-cyber-text transition-colors">
              <XCircle size={12} />
            </button>
          )}
        </div>

        {/* Filters row */}
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <div className="flex items-center gap-1">
            <Filter size={12} className="text-cyber-muted" />
            <select
              value={severityFilter}
              onChange={(e) => { setSeverityFilter(e.target.value); setCurrentPage(1); }}
              aria-label="Filter by severity level"
              className="bg-[#131620] border border-cyber-border rounded-lg px-2.5 py-1.5 text-xs text-cyber-text font-mono outline-none hover:border-cyber-blue/30 transition-colors cursor-pointer"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="info">Info</option>
            </select>
          </div>

          <select
            value={outcomeFilter}
            onChange={(e) => { setOutcomeFilter(e.target.value); setCurrentPage(1); }}
            aria-label="Filter by outcome"
            className="bg-[#131620] border border-cyber-border rounded-lg px-2.5 py-1.5 text-xs text-cyber-text font-mono outline-none hover:border-cyber-blue/30 transition-colors cursor-pointer"
          >
            <option value="all">All Outcomes</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="blocked">Blocked</option>
          </select>

          <div className="text-[11px] text-cyber-muted font-mono ml-auto sm:ml-0">
            <span>{Math.min((currentPage - 1) * rowsPerPage + 1, filtered.length)}–{Math.min(currentPage * rowsPerPage, filtered.length)} / {filtered.length}</span>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedRows.size > 0 && (
        <div className="bg-cyber-blue/10 border border-cyber-blue/25 rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-3 fade-in-up">
          <span className="text-xs font-medium text-cyber-blue">{selectedRows.size} rows selected</span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-1.5 text-xs text-cyber-red hover:text-cyber-red/80 transition-colors"
          >
            <Trash2 size={12} />
            Flag for Review
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 text-xs text-cyber-muted hover:text-cyber-text transition-colors"
          >
            <Download size={12} />
            <span className="hidden sm:inline">Export Selected (CSV)</span>
            <span className="sm:hidden">CSV</span>
          </button>
          <button
            onClick={handleExportJSON}
            className="flex items-center gap-1.5 text-xs text-cyber-muted hover:text-cyber-text transition-colors"
          >
            <Download size={12} />
            <span className="hidden sm:inline">Export Selected (JSON)</span>
            <span className="sm:hidden">JSON</span>
          </button>
          <button
            onClick={() => setSelectedRows(new Set())}
            className="ml-auto text-xs text-cyber-muted hover:text-cyber-text transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── Mobile Card View (< md) ── */}
      <div className="md:hidden space-y-2">
        {error ? (
          <div className="cyber-card px-5 py-10 text-center">
            <p className="text-xs text-cyber-red font-mono">Failed to load audit logs</p>
          </div>
        ) : loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="cyber-card p-3 space-y-2" aria-busy="true">
              <div className="flex justify-between">
                <div className="w-32 h-3 rounded skeleton-cyber" />
                <div className="w-14 h-4 rounded-full skeleton-cyber" />
              </div>
              <div className="w-24 h-2.5 rounded skeleton-cyber" />
              <div className="flex gap-2">
                <div className="w-16 h-4 rounded-md skeleton-cyber" />
                <div className="w-16 h-4 rounded-md skeleton-cyber" />
              </div>
            </div>
          ))
        ) : (
          <>
            {paginated.map((log) => (
              <MobileLogCard
                key={log.id}
                log={log}
                selected={selectedRows.has(log.id)}
                onToggle={() => toggleRow(log.id)}
              />
            ))}
            {paginated.length === 0 && (
              <div className="cyber-card px-4 py-8 text-center text-xs text-cyber-muted font-mono">
                No audit logs match current filters
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Desktop/Tablet Table View (md+) ── */}
      <div className="hidden md:block cyber-card overflow-hidden">
        {error ? (
          <div className="px-5 py-10 text-center">
            <p className="text-xs text-cyber-red font-mono">Failed to load audit logs</p>
          </div>
        ) : loading ? (
          <div className="overflow-x-auto scrollbar-cyber" aria-busy="true" aria-label="Loading audit logs">
            <table className="w-full text-xs min-w-[1100px]" role="table">
              <thead>
                <tr className="border-b border-cyber-border bg-[#0A0D14]" role="row">
                  <th className="px-3 py-3 w-8" scope="col">
                    <div className="w-4 h-4 rounded skeleton-cyber" />
                  </th>
                  {['Timestamp', 'Actor', 'Action', 'Resource', 'IP', 'Location', 'Risk', 'MFA', 'Session', 'Outcome', 'Actions'].map((col) => (
                    <th key={col} scope="col" className="px-3 py-3 text-left">
                      <div className="w-16 h-2.5 rounded skeleton-cyber" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-cyber-border/40">
                {Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} role="row">
                    <td className="px-3 py-3"><div className="w-4 h-4 rounded skeleton-cyber" /></td>
                    <td className="px-3 py-3">
                      <div className="w-16 h-3 rounded skeleton-cyber mb-1" />
                      <div className="w-20 h-2.5 rounded skeleton-cyber" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="w-24 h-3 rounded skeleton-cyber mb-1" />
                      <div className="w-16 h-2.5 rounded skeleton-cyber" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="w-28 h-3 rounded skeleton-cyber mb-1" />
                      <div className="w-12 h-3 rounded skeleton-cyber" />
                    </td>
                    <td className="px-3 py-3"><div className="w-32 h-3 rounded skeleton-cyber" /></td>
                    <td className="px-3 py-3"><div className="w-24 h-3 rounded skeleton-cyber" /></td>
                    <td className="px-3 py-3"><div className="w-20 h-3 rounded skeleton-cyber" /></td>
                    <td className="px-3 py-3"><div className="w-14 h-4 rounded-full skeleton-cyber" /></td>
                    <td className="px-3 py-3"><div className="w-16 h-4 rounded-md skeleton-cyber" /></td>
                    <td className="px-3 py-3"><div className="w-20 h-3 rounded skeleton-cyber" /></td>
                    <td className="px-3 py-3"><div className="w-16 h-4 rounded-md skeleton-cyber" /></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <div className="w-7 h-7 rounded-lg skeleton-cyber" />
                        <div className="w-7 h-7 rounded-lg skeleton-cyber" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto scrollbar-cyber">
            <table className="w-full text-xs min-w-[1100px]" role="table" aria-label="Security audit logs">
              <thead>
                <tr className="border-b border-cyber-border bg-[#0A0D14]" role="row">
                  <th className="px-3 py-3 w-8" scope="col" aria-label="Select all rows">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === paginated.length && paginated.length > 0}
                      onChange={toggleAll}
                      aria-label="Select all rows on this page"
                      className="rounded border-cyber-border accent-cyber-blue cursor-pointer"
                    />
                  </th>
                  {[
                    { key: 'event_timestamp', label: t(language, 'logs_timestamp') },
                    { key: 'actor', label: t(language, 'logs_actor') },
                    { key: 'action', label: t(language, 'logs_action') },
                    { key: 'resource', label: t(language, 'logs_resource') },
                    { key: 'ip_address', label: t(language, 'logs_ip') },
                    { key: 'location', label: t(language, 'logs_location') },
                    { key: 'risk_level', label: t(language, 'logs_risk') },
                    { key: 'mfa_status', label: t(language, 'logs_mfa') },
                    { key: 'session_id', label: t(language, 'logs_session') },
                    { key: 'outcome', label: t(language, 'logs_outcome') },
                  ].map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      onClick={() => handleSort(col.key as SortField)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(col.key as SortField); } }}
                      tabIndex={0}
                      aria-sort={sortField === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      aria-label={`Sort by ${col.label}`}
                      className={`px-3 py-3 text-left text-[10px] font-mono uppercase tracking-widest whitespace-nowrap cursor-pointer hover:text-cyber-blue transition-colors select-none focus:outline-none focus:text-cyber-blue ${
                        sortField === col.key ? 'text-cyber-blue' : 'text-cyber-muted'
                      } ${language === 'bn' ? 'font-bangla normal-case tracking-normal text-[11px]' : ''}`}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        <SortIcon field={col.key as SortField} />
                      </div>
                    </th>
                  ))}
                  <th scope="col" className="px-3 py-3 text-right text-[10px] font-mono text-cyber-muted uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cyber-border/40">
                {paginated.map((log) => {
                  const ts = formatTimestamp(log.event_timestamp);
                  return (
                    <tr
                      key={log.id}
                      role="row"
                      aria-selected={selectedRows.has(log.id)}
                      className={`hover:bg-white/3 transition-colors duration-100 group ${
                        selectedRows.has(log.id) ? 'bg-cyber-blue/5' : ''
                      }`}
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(log.id)}
                          onChange={() => toggleRow(log.id)}
                          aria-label={`Select log ${log.log_id}`}
                          className="rounded border-cyber-border accent-cyber-blue cursor-pointer"
                        />
                      </td>

                      {/* Timestamp */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className="font-mono text-[11px] text-cyber-text">{ts.time}</p>
                        <p className="font-mono text-[10px] text-cyber-muted">{ts.date}</p>
                      </td>

                      {/* Actor */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className="text-cyber-text font-medium">{log.actor}</p>
                        <p className="text-[10px] text-cyber-muted font-mono">{log.actor_role}</p>
                      </td>

                      {/* Action */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className={`font-medium ${actionTypeColor[log.action_type] || ''}`}>{log.action}</p>
                        <span className="text-[9px] font-mono bg-white/5 border border-cyber-border rounded px-1 text-cyber-subtle capitalize">
                          {log.action_type}
                        </span>
                      </td>

                      {/* Resource */}
                      <td className="px-3 py-3 max-w-[160px]">
                        <p className="font-mono text-[11px] text-cyber-muted truncate" title={log.resource}>
                          {log.resource}
                        </p>
                      </td>

                      {/* IP */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className="font-mono text-[11px] text-cyber-text">{log.ip_address}</p>
                      </td>

                      {/* Location */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className="text-[11px] text-cyber-muted">{log.location}</p>
                      </td>

                      {/* Risk Level */}
                      <td className="px-3 py-3">
                        <span className={riskBadge[log.risk_level] || 'cyber-badge-info'}>
                          {log.risk_level}
                        </span>
                      </td>

                      {/* MFA Status */}
                      <td className="px-3 py-3">
                        <span className={`text-[10px] font-mono font-medium border rounded-md px-2 py-0.5 ${mfaBadge[log.mfa_status] || ''}`}>
                          {log.mfa_status}
                        </span>
                      </td>

                      {/* Session ID */}
                      <td className="px-3 py-3">
                        <p className="font-mono text-[11px] text-cyber-subtle">{log.session_id}</p>
                      </td>

                      {/* Outcome */}
                      <td className="px-3 py-3">
                        <span className={`flex items-center gap-1 text-[10px] font-mono font-medium rounded-md px-2 py-0.5 w-fit ${outcomeConfig[log.outcome]?.color || ''}`}>
                          {outcomeConfig[log.outcome]?.icon}
                          {log.outcome}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="View log details"
                            aria-label={`View details for log ${log.log_id}`}
                            className="w-7 h-7 rounded-lg bg-white/5 hover:bg-cyber-blue/15 hover:text-cyber-blue flex items-center justify-center transition-all duration-150 text-cyber-muted"
                          >
                            <Eye size={12} />
                          </button>
                          <button
                            title="More options"
                            aria-label={`More options for log ${log.log_id}`}
                            className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all duration-150 text-cyber-muted"
                          >
                            <MoreHorizontal size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {paginated.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-xs text-cyber-muted font-mono">
                      No audit logs match current filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && (
          <div className="px-4 py-3 border-t border-cyber-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[11px] text-cyber-muted font-mono">
              <span>{t(language, 'logs_total')}: {filtered.length}</span>
              <span>·</span>
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                aria-label="Rows per page"
                className="bg-[#131620] border border-cyber-border rounded px-2 py-0.5 text-[11px] text-cyber-text font-mono outline-none cursor-pointer"
              >
                {[5, 10, 20, 50].map(n => (
                  <option key={n} value={n}>{n} per page</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1" role="navigation" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                aria-label="First page"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-cyber-muted hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-mono text-[11px]"
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-cyber-muted hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={13} />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    aria-label={`Page ${page}`}
                    aria-current={currentPage === page ? 'page' : undefined}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-mono transition-all duration-150 ${
                      currentPage === page
                        ? 'bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30' :'text-cyber-muted hover:bg-white/5'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                aria-label="Next page"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-cyber-muted hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={13} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                aria-label="Last page"
                className="w-7 h-7 rounded-lg flex items-center justify-center text-cyber-muted hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-mono text-[11px]"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Pagination */}
      <div className="md:hidden">
        {!loading && !error && totalPages > 1 && (
          <div className="flex items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2 text-[11px] text-cyber-muted font-mono">
              <select
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                aria-label="Rows per page"
                className="bg-[#131620] border border-cyber-border rounded px-2 py-1 text-[11px] text-cyber-text font-mono outline-none cursor-pointer"
              >
                {[5, 10, 20].map(n => (
                  <option key={n} value={n}>{n}/page</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1" role="navigation" aria-label="Pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-cyber-muted bg-cyber-surface border border-cyber-border hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-[11px] font-mono text-cyber-muted px-2">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-cyber-muted bg-cyber-surface border border-cyber-border hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}